// Cross-tab invalidation over BroadcastChannel (architecture sprint-04 §5.2, S5.8). Messages carry
// no data, only "refetch this" signals. A tab never receives its own messages. Without
// BroadcastChannel (Safari < 15.4) everything is a no-op and the 30 s polling covers it.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export type SyncMessage =
  | { type: 'timer-changed' }
  | { type: 'shift-changed' }
  | { type: 'entries-changed' }
  | { type: 'prefs-changed' }
  | { type: 'checkin-answered'; slotId: string }
  /** Sprint 14: I'm awake was pressed in another tab: stop ringing up to `upTo` for this shift. */
  | { type: 'alarm-confirmed'; shiftId: string; upTo: string };

const CHANNEL_NAME = 'time-tracker-sync';

let channel: BroadcastChannel | null = null;
const listeners = new Set<(msg: SyncMessage) => void>();

function isSyncMessage(value: unknown): value is SyncMessage {
  if (typeof value !== 'object' || value === null) return false;
  const type = (value as { type?: unknown }).type;
  if (type === 'checkin-answered') return typeof (value as { slotId?: unknown }).slotId === 'string';
  if (type === 'alarm-confirmed') {
    const v = value as { shiftId?: unknown; upTo?: unknown };
    return typeof v.shiftId === 'string' && typeof v.upTo === 'string';
  }
  return type === 'timer-changed' || type === 'shift-changed' || type === 'entries-changed' || type === 'prefs-changed';
}

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<unknown>): void => {
      if (!isSyncMessage(event.data)) return;
      for (const fn of listeners) fn(event.data);
    };
  } catch {
    channel = null;
  }
  return channel;
}

export function broadcast(msg: SyncMessage): void {
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage(msg);
  } catch {
    // A closed or unavailable channel only loses the instant sync; polling still covers it.
  }
}

export function subscribe(fn: (msg: SyncMessage) => void): () => void {
  getChannel();
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Test seam: drops the cached channel and listeners so a stubbed BroadcastChannel is picked up. */
export function resetSyncForTests(): void {
  try {
    channel?.close();
  } catch {
    // ignore
  }
  channel = null;
  listeners.clear();
}

/** Mounted once in AppShell: maps messages from other tabs to query invalidations. */
export function useSyncListener(): void {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      subscribe((msg) => {
        if (msg.type === 'timer-changed') {
          void queryClient.invalidateQueries({ queryKey: ['timer'] });
          void queryClient.invalidateQueries({ queryKey: ['entries'] });
        } else if (msg.type === 'shift-changed') {
          // Sprint 13: the open shift rides on GET /api/timer, so refreshing the timer refreshes it.
          void queryClient.invalidateQueries({ queryKey: ['timer'] });
          void queryClient.invalidateQueries({ queryKey: ['shifts'] });
          void queryClient.invalidateQueries({ queryKey: ['entries'] });
        } else if (msg.type === 'entries-changed') {
          void queryClient.invalidateQueries({ queryKey: ['entries'] });
          void queryClient.invalidateQueries({ queryKey: ['projects'] });
          void queryClient.invalidateQueries({ queryKey: ['admin', 'projects'] });
        } else if (msg.type === 'prefs-changed') {
          void queryClient.invalidateQueries({ queryKey: ['me'] });
        }
      }),
    [queryClient],
  );
}
